import { plans } from "@/lib/lottery-data";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { usePlanPrices } from "@/hooks/use-plan-prices";
import PromoBanner from "@/components/PromoBanner";

interface PricingProps {
  onSignup: (intendedTier?: "free" | "pro" | "premium" | null) => void;
}

const PricingSection = ({ onSignup }: PricingProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { prices } = usePlanPrices();

  const formatPriceParts = (value: number | null | undefined): { price: string; sub: string | null } => {
    if (value === null || value === undefined) return { price: "—", sub: null };
    if (value === 0) return { price: "R$ 0", sub: null };
    const intPart = Math.trunc(value);
    const cents = Math.round((value - intPart) * 100);
    return {
      price: `R$ ${intPart}`,
      sub: cents > 0 ? `,${cents.toString().padStart(2, "0")}/mês` : "/mês",
    };
  };

  const handleClick = (tier: "free" | "pro" | "premium") => {
    if (tier === "free") {
      onSignup("free");
      return;
    }
    // Para planos pagos: leva pra /planos?tier=... que dispara o checkout
    // automaticamente se o usuário já estiver logado, ou abre o cadastro.
    if (!user) {
      onSignup(tier);
      return;
    }
    navigate(`/planos?tier=${tier}&autostart=1`);
  };

  return (
    <div className="mx-auto max-w-[900px] px-8 pb-[72px]" id="planos">
      <PromoBanner />
      <div className="mb-3 mt-2 text-[10px] font-medium uppercase tracking-[0.14em] text-primary">Planos</div>
      <h2 className="mb-3.5 font-serif text-[clamp(26px,3.5vw,46px)] font-light leading-[1.15] text-foreground">
        Escolha seu nível <em className="text-primary italic">de análise</em>
      </h2>
      <p className="mb-9 max-w-[500px] text-[15px] leading-relaxed text-lt-cream3">
        Comece gratuitamente e evolua quando precisar de mais profundidade
      </p>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {plans.map((plan, i) => {
          const dynamic = formatPriceParts(prices[plan.tier]?.monthly);
          const displayPrice = plan.tier === "free" ? plan.price : dynamic.price;
          const displaySub = plan.tier === "free" ? (plan.priceSub || plan.period) : (dynamic.sub ?? plan.period);
          return (
          <div
            key={i}
            className={cn(
              "relative rounded-2xl border p-6 transition-colors",
              plan.popular ? "border-lt-gold-border bg-lt-gold-bg" : "border-lt-border bg-lt-bg2"
            )}
          >
            {plan.popular && (
              <div className="mb-4 inline-block rounded-full border border-lt-gold-border bg-lt-gold-bg px-3 py-1 text-[9px] font-medium uppercase tracking-wider text-primary">
                Mais popular
              </div>
            )}
            <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-lt-cream3">{plan.name}</div>
            <div className="mb-5 font-serif text-[36px] font-light text-foreground">
              {displayPrice}
              <sub className="text-[14px] font-normal text-lt-cream3">{displaySub}</sub>
            </div>
            <ul className="mb-6 space-y-2">
              {plan.items.map((item, j) => (
                <li key={j} className="flex items-start gap-2 text-[13px] text-lt-cream3">
                  <span className="mt-0.5 text-lt-teal">✓</span>
                  {item}
                </li>
              ))}
            </ul>
            <button
              onClick={() => handleClick(plan.tier)}
              className={cn(
                "w-full rounded-lg py-3 font-sans text-[13px] font-medium transition-colors",
                plan.variant === "default"
                  ? "bg-primary text-primary-foreground hover:opacity-90"
                  : "border border-lt-border text-lt-cream2 hover:border-lt-cream3"
              )}
            >
              {plan.cta}
            </button>
          </div>
          );
        })}
      </div>
    </div>
  );
};

export default PricingSection;
